import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { ThemeProvider } from 'styled-components';
import { useHistory } from 'react-router';
import {
  Row,
  Spin,
  Card,
  Button,
  Col,
  Grid,
  message,
} from 'antd';
import {
  EyeOutlined,
  ArrowLeftOutlined,
  FileDoneOutlined,
  ArrowRightOutlined,
} from '@ant-design/icons';
import CustomTable from 'components/Table';
import Previewer from 'components/Previewer';
import { SET_LAST_PAYMENT_DATA, SET_RECIBOS } from 'store/reducers/caja';
import { defSigob } from 'themes';
import GlobalStyles from 'themes/global';
import {
  getManyRecibos,
  getRecibosPDF,
} from 'api/recaudacion/recibos';
import { DATE_FRONTEND_FORMAT } from 'utils/formatValues';
import { sendPDFToEmail } from 'api/configuracion';
import { getFileName } from 'utils/getFileName';
import useQuery from 'hooks/useQuery';
import ModalFacturacion from './ModalFacturacion';

const { useBreakpoint } = Grid;

const Recibos = () => {
  const screens = useBreakpoint();
  const dispatch = useDispatch();
  const history = useHistory();
  const [from] = useQuery(['from']);
  const { recibos } = useSelector(({ caja }) => caja);
  const { theme } = useSelector(({ app }) => app);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [visiblePreview, setVisiblePreview] = useState(false);
  const [base64, setBase64] = useState('');
  const [ciudadano, setCiudadano] = useState();
  const [visibleModal, setVisibleModal] = useState(false);
  const [recibosAFacturar, setRecibosAFacturar] = useState([]);
  const [visiblePreviewPDF, setVisiblePreviewPDF] = useState(false);
  const [IFrameURL, setIFrameURL] = useState();

  const [isGoBackPending, setIsGoBackPending] = useState(false);

  const fetchData = async (_formFacturacionValues) => {
    setLoading(true);
    const datos = await getManyRecibos(recibos);
    setData(datos);
    setLoading(false);
    const recibosConXML = datos.filter((e) => e.xml_archivo);
    if (_formFacturacionValues?.email && recibosConXML.length) {
      const idsToSent = recibosConXML.map((recibo) => recibo.id);
      const oldURL = recibosConXML[0]?.xml_archivo;
      // const baseURL = 'https://demosigob.obs.la-north-2.myhuaweicloud.com/demosigob/media/';
      // eslint-disable-next-line max-len
      // const updatedURL = oldURL.replace(/^http:\/\/obsdemos\.migob\.digital\/demosigob\/media\//, baseURL);
      const baseURL = 'https://mun-oaxaca.obs.la-north-2.myhuaweicloud.com/mun-oaxaca/media/';
      const updatedURL = oldURL.replace(/^http:\/\/obs-mun-oaxaca\.migob\.digital\/mun-oaxaca\/media\//, baseURL);
      const url_xml = `${updatedURL.split('.xml')[0]}.xml`;
      // const base64XMLs = await Promise.all(promisesToDecode);
      const pdfs = await getRecibosPDF(idsToSent);
      if (pdfs.success) {
        const archivo_base64 = pdfs.base64.replace('data:application/pdf;base64,', '');
        await sendPDFToEmail({
          archivo_base64,
          email: _formFacturacionValues.email,
          reporte_redaccion: 'Comprobante de Pago',
          nombre_del_archivo: 'Comprobante de Pago',
          url_xml,
        });
      }
      // const promises = base64XMLs.map((archivo_base64) => sendPDFToEmail({
      //   archivo_base64,
      //   email: _formFacturacionValues.email,
      //   reporte_redaccion: 'Comprobante de Pago',
      //   nombre_del_archivo: 'Comprobante de Pago',
      // }));

      // await Promise.all(promises);
    }
  };

  useEffect(() => {
    let mounted = true;
    const _fetchData = async () => {
      setLoading(true);
      const datos = await getManyRecibos(recibos);
      if (mounted) {
        setData(datos);
        setLoading(false);
      }
      if (datos) {
        const idsRecibos = datos.map((e) => e.id);
        setRecibosAFacturar(idsRecibos);
        setVisibleModal(true);
      }
    };
    _fetchData();
    return () => { mounted = false; };
    // eslint-disable-next-line
  }, [])

  const columnsRecibo = [
    {
      titleText: 'Folio',
      dataIndex: 'folio',
      key: 'folio',
      render: (val, record) => val || record.id,
      width: 150,
    },
    {
      titleText: 'Folio de Facturación',
      dataIndex: 'folio_de_facturacion',
      key: 'folio_de_facturacion',
      width: 150,
    },
    {
      titleText: 'Fecha',
      dataIndex: 'fecha_de_creacion',
      key: 'fecha_de_creacion',
      render: (val) => val?.format?.(DATE_FRONTEND_FORMAT) || val,
      width: 100,
    },
    {
      titleText: 'Facturado',
      dataIndex: 'xml_archivo',
      key: 'xml_archivo',
      width: 100,
      render: (val) => (val ? 'Si' : 'No'),
    },
    {
      titleText: 'Padrón',
      dataIndex: 'padron_nombre_completo',
      key: 'padron_nombre_completo',
      width: 300,
    },
    {
      titleText: 'Tipo de Padrón',
      dataIndex: 'tipo_padron',
      key: 'tipo_padron',
      width: 150,
    },
    {
      titleText: 'Importe',
      dataIndex: 'importe_total',
      key: 'importe_total',
      align: 'right',
      width: 150,
    },
  ];

  const onClickPreview = async () => {
    setLoading(true);
    const result = await getRecibosPDF(selectedRowKeys);
    if (result.success) {
      setBase64(result.base64);
      setVisiblePreview(true);
    }
    setLoading(false);
  };

  const rowSelection = {
    selectedRowKeys,
    type: 'checkbox',
    onChange: setSelectedRowKeys,
  };

  const onRow = (record) => ({
    onClick: () => {
      if (selectedRowKeys.includes(record.id)) {
        const clone = [...selectedRowKeys];
        clone.splice(selectedRowKeys.indexOf(record.id), 1);
        setSelectedRowKeys(clone);
      } else {
        setSelectedRowKeys([...selectedRowKeys, record.id]);
      }
      setCiudadano(record);
    },
  });
  // #TOPERSIST SE QUEDA POR SI PIDEN TICKETS NADA MAS DESCOMENTAR
  // const onClickGetTickets = async () => {
  //   setLoading(true);
  //   const results = await getTickets(selectedRowKeys);
  //   if (results.success) {
  //     setBase64(results.base64);
  //     setVisiblePreview(true);
  //   }
  //   setLoading(false);
  // };

  const onClickGetFactura = () => {
    const _recibos = data.filter((e) => selectedRowKeys.includes(e.id));
    const _recibosAfacturar = _recibos.filter((e) => !e.xml_archivo).map((e) => e.id);
    if (_recibosAfacturar.length) {
      setRecibosAFacturar(_recibosAfacturar);
      setVisibleModal(true);
    } else {
      message.info('el recibo ya esta facturado');
    }
  };

  // const onClickPreviewFactura = async () => {
  //   setLoading(true);
  //   const _recibos = data.filter((e) => selectedRowKeys.includes(e.id));
  //   const _facturados = _recibos.filter((r) => r.pdf_de_rfc);
  //   if (_facturados.length > 1) {
  //     message.warn('Seleccione solo un recibo para ver su Factura');
  //     return;
  //   } if (!_facturados?.length) {
  //     message.warn('El recibo seleccionado no está facturado');
  //     return;
  //   }
  //   const result = await getFacturaPDF(selectedRowKeys);
  //   if (result.success) {
  //     setBase64(result.base64);
  //     setVisiblePreview(true);
  //   }
  //   setLoading(false);
  // };

  const onClickXML = async () => {
    setLoading(true);
    const _recibos = data.filter((e) => selectedRowKeys.includes(e.id));
    const _facturados = _recibos.filter((r) => r.xml_archivo);
    if (_facturados.length > 1) {
      message.warn('Seleccione solo un recibo para obtener su XML');
      setLoading(false);
      return;
    } if (!_facturados?.length) {
      message.warn('El recibo seleccionado no está facturado');
      setLoading(false);
      return;
    }
    const oldURL = _facturados[0].xml_archivo;
    // const baseURL = 'https://demosigob.obs.la-north-2.myhuaweicloud.com/demosigob/media/';
    // eslint-disable-next-line max-len
    // const updatedURL = oldURL.replace(/^http:\/\/obsdemos\.migob\.digital\/demosigob\/media\//, baseURL);
    const baseURL = 'https://mun-oaxaca.obs.la-north-2.myhuaweicloud.com/mun-oaxaca/media/';
    const updatedURL = oldURL.replace(/^http:\/\/obs-mun-oaxaca\.migob\.digital\/mun-oaxaca\/media\//, baseURL);
    const croppedURL = `${updatedURL.split('.xml')[0]}.xml`;
    window.open(croppedURL);
    setLoading(false);
  };

  const clear = () => {
    dispatch({
      type: SET_RECIBOS,
      payload: [],
    });
    dispatch({
      type: SET_LAST_PAYMENT_DATA,
      payload: [],
    });
  };

  const backPaths = {
    caja: '/recaudacion/caja',
    registro_de_pagos: '/recaudacion/registro-de-pagos',
  };

  /**
 * Maneja la navegación hacia atrás desde la vista actual.
 * - Limpia el estado relacionado con los recibos.
 * - Si hay historial de navegación, regresa a la página anterior.
 * - Si no se encuentra una ruta válida, redirige al dashboard.
 */
  const handleGoBack = () => {
    clear();

    if (history.length > 1) {
      setIsGoBackPending(true); // Indica que la acción de regresar está en progreso.
      history.goBack(); // Navega a la página anterior.

      // Por si llega a fallar el goBack nos esperamos 1.5 segundos y redirigimos con un push
      if (backPaths[from]) {
        setTimeout(() => {
          history.push(backPaths[from]); // Redirige a una de las rutas en las que se puede
          // cobrar en caso de que este el param from
        }, 1500);
      } else {
        message.warn('No se encontró la ruta de regreso, redirigiendo al dashboard.');
        history.push('/dashboard/'); // Redirige al dashboard como fallback.
      }
    }
  };

  return (
    <ThemeProvider theme={theme || defSigob}>
      <GlobalStyles />
      <br />
      <Row align="center" justify="center" className="form-Container">
        <Spin tip="Cargando..." spinning={loading}>
          <Card
            className="customCard"
            title={(
              <Row justify="space-between">
                <Col>
                  <Button
                    className="primary-on-hover"
                    icon={<ArrowLeftOutlined />}
                    loading={isGoBackPending}
                    onClick={handleGoBack}
                  >
                    {screens.md ? 'Regresar a la Caja' : 'Caja'}
                  </Button>
                </Col>
                <Col>
                  <Button
                    className="primary-on-hover"
                    icon={<ArrowRightOutlined />}
                    onClick={() => {
                      clear();
                      history.push('/dashboard/');
                    }}
                  >
                    {`${screens.md ? 'Ir al Dashboard' : 'Dashboard'}`}
                  </Button>
                </Col>
              </Row>
            )}
          >
            <CustomTable
              cols={columnsRecibo}
              data={data}
              rowSelection={rowSelection}
              loading={loading}
              onRow={onRow}
              allowSearch={false}
              allowSort={false}
              allowAdd={false}
              allowDelete={false}
              allowEdit={false}
              allowExport={false}
              allowExpand={false}
              allowPreview
              rowKey="id"
              hasAllViewPermission
              customActions={[
                {
                  onClick: onClickPreview,
                  icon: <EyeOutlined />,
                  text: 'Ver comprobante de pago',
                },
                // {
                //   onClick: onClickPreviewFactura,
                //   icon: <EyeOutlined />,
                //   text: 'Ver Factura',
                // },
                {
                  onClick: onClickXML,
                  icon: <FileDoneOutlined />,
                  text: 'Obtener XML',
                },
                {
                  onClick: onClickGetFactura,
                  icon: <FileDoneOutlined />,
                  text: 'Facturar',
                },
              ]}
            />
          </Card>
        </Spin>
      </Row>
      <Previewer
        visible={visiblePreview}
        setVisible={setVisiblePreview}
        base64={base64}
        allowSend
        allowDownload
        id={selectedRowKeys[0]}
        baseURL="recaudacion/recibos/"
        cuentaUnicaID={ciudadano?.cuenta_unica?.id}
        email={[...new Set(data.map((item) => item.padron.email))].filter((item) => item)[0]}
        customFileNameFormat={() => getFileName({
          data: selectedRowKeys.length === 1
            ? data.find((e) => e.id === selectedRowKeys[0]) : null,
          tipoDeDocumento: 'Recibo',
        })}
      />
      <Previewer
        visible={visiblePreviewPDF}
        allowDownload
        customFileNameFormat={() => getFileName({
          data: selectedRowKeys.length === 1
            ? data.find((e) => e.id === selectedRowKeys[0]) : null,
          tipoDeDocumento: 'Documento 1',
        })}
        setVisible={() => {
          setVisiblePreviewPDF(false);
          setIFrameURL();
        }}
        iFrameUrl={IFrameURL}
        cuentaUnicaID={ciudadano?.cuenta_unica?.id}
        email={[...new Set(data.map((item) => item.padron?.email))].filter((item) => item)[0]}
      />
      <ModalFacturacion
        visible={visibleModal}
        setVisible={setVisibleModal}
        recibosAFacturar={recibosAFacturar}
        callback={fetchData}
      />
    </ThemeProvider>
  );
};

export default Recibos;
